/**
 * T155P1 — Review Cockpit Redux slice.
 *
 * Manages the review queue state (4 attention buckets) and exposes:
 *   - fetchReviewQueue  — GET /transactions/review-queue
 *   - batchMarkReviewed — POST /transactions/batch/mark-reviewed
 *   - removeItemFromQueue — optimistic removal action
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { ReviewQueue, BatchMarkReviewedResult } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface ReviewState {
  queue: ReviewQueue | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ReviewState = {
  queue: null,
  isLoading: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchReviewQueue = createAsyncThunk(
  'review/fetchReviewQueue',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.transactions.reviewQueue);
      return (response.data.data ?? response.data) as ReviewQueue;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch review queue'));
    }
  }
);

export const batchMarkReviewed = createAsyncThunk(
  'review/batchMarkReviewed',
  async ({ transactionIds, dryRun }: { transactionIds: string[]; dryRun?: boolean }, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.transactions.batchMarkReviewed, { transactionIds, dryRun });
      return (response.data.data ?? response.data) as BatchMarkReviewedResult;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to mark transactions as reviewed'));
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const reviewSlice = createSlice({
  name: 'review',
  initialState,
  reducers: {
    /**
     * Optimistically remove a transaction ID from all buckets.
     * Called immediately after a successful mark-reviewed action
     * so the item visually leaves the list without waiting for a refetch.
     */
    removeItemFromQueue(state, action: PayloadAction<string>) {
      if (!state.queue) return;
      const id = action.payload;
      const buckets = ['uncategorized', 'suspicious', 'duplicateLike', 'recurringLinked'] as const;
      for (const bucket of buckets) {
        const before = state.queue[bucket].count;
        state.queue[bucket].items = state.queue[bucket].items.filter((t) => t.id !== id);
        const after = state.queue[bucket].items.length;
        if (before !== after) {
          state.queue[bucket].count = after;
          state.queue.total = Math.max(0, state.queue.total - (before - after));
        }
      }
    },
    /**
     * Optimistically remove multiple transaction IDs from all buckets.
     */
    removeItemsFromQueue(state, action: PayloadAction<string[]>) {
      if (!state.queue) return;
      const ids = new Set(action.payload);
      const buckets = ['uncategorized', 'suspicious', 'duplicateLike', 'recurringLinked'] as const;
      for (const bucket of buckets) {
        const before = state.queue[bucket].count;
        state.queue[bucket].items = state.queue[bucket].items.filter((t) => !ids.has(t.id));
        const after = state.queue[bucket].items.length;
        if (before !== after) {
          state.queue[bucket].count = after;
          state.queue.total = Math.max(0, state.queue.total - (before - after));
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReviewQueue.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReviewQueue.fulfilled, (state, action) => {
        state.isLoading = false;
        state.queue = action.payload;
      })
      .addCase(fetchReviewQueue.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { removeItemFromQueue, removeItemsFromQueue } = reviewSlice.actions;
export default reviewSlice.reducer;
